import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AppShell } from "@/components/AppShell";
import { DotRail } from "@/components/DotRail";
import { PokemonSprite } from "@/components/PokemonSprite";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { usePrices, formatPlanLine } from "@/hooks/usePrices";
import {
  Copy, UserPlus, ChevronRight, Send, ArrowRight,
  Sparkles, X, Check, Users, Wand2, ChevronLeft, QrCode, ScanLine,
  KeyRound, Share2, ChevronDown, ChevronUp, Lock, Trophy,
  Loader2, UserCheck, UserX, AtSign, Inbox, Settings2, Star,
} from "lucide-react";
import { TradeSwapIcon } from "@/components/icons/TradeSwapIcon";
import {
  TOTAL_RELEASED, type Friend,
} from "@/lib/friends";
import { dexPercent } from "@/lib/constants";
import { deriveDexCounts } from "@/lib/dexCounts";
import { DEX_STATE_EVENT } from "@/lib/dexStateStore";
import { DEX_TOTALS_EVENT } from "@/lib/dexQueryRefresh";
import { useSession } from "@/components/SessionProvider";
import { isFriendLimitError, errorMessage } from "@/lib/apiError";
import { useAuth } from "@/components/AuthProvider";
import { requestProfileSetup } from "@/lib/profileSetupRequest";
import { firstInitial, trainerHandle, trainerInitial } from "@/lib/trainerIdentity";
import { QrScanTab } from "@/components/QrScanTab";
import { useLocation } from "wouter";
import type { DexCategory } from "@/lib/data";
import { QRCodeSVG } from "qrcode.react";
import { type PokemonDexEntry } from "@/lib/pokemon";
import { orderByPriority, priorityFriendIds } from "@/lib/priorityFriends";
import { IndexedKpiPill } from "@/components/IndexedKpiBadge";
import { ProLock } from "@/components/ProLock";
import { hasProAccess } from "@/components/SessionProvider";
import { MenuHeading } from "@/components/MenuHeading";
import { WishlistStrip } from "@/components/WishlistStrip";
import { HeadToHeadBar } from "@/components/HeadToHeadBar";
import { useWishlist } from "@/lib/wishlistStore";
import { dexNumberLabel } from "@/lib/dexNumberLabel";

const YOUR_CONNECT_URL = "nexusdex.ai/connect/X9P3K2A8";

export default function Friends() {
  // Friends is fully accessible in Basic. Pro depth (the full dex-completion
  // matrix, advanced trade-string operators) is teased per-feature inside the
  // page, not at the gate.
  const { openShare } = useSession();
  const [viewingFriend, setViewingFriend] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  if (viewingFriend) {
    return (
      <AppShell pageSpacing>
        <FriendDexView
          friendId={viewingFriend}
          onBack={() => setViewingFriend(null)}
          onGenerate={(cat) => {
            setLocation(`/trade?for=${viewingFriend}&dex=${cat}`);
          }}
        />
      </AppShell>
    );
  }

  return (
    <AppShell pageSpacing>
      {/* b13i I1: decorative inverse-theme dot-rail down the page gutter. */}
      <DotRail>
      {/* Hero header — page header carries its own eyebrow dot, so it opts out
          of the per-block rail dot. */}
      {/* b17s S2: drop the extra mt-2 so the hero top spacing is exactly
          `relative pt-12`, matching Profile and Calendar. The stray 8px top
          margin made Friends read a touch tighter/different from the other
          menus once the dot-rail and aurora were in place. */}
      <section className="relative pt-12 page-hero-top" data-testid="section-friends-hero" data-dotrail-skip>
        <div className="absolute inset-0 pointer-events-none -z-10" aria-hidden>
          <div className="v2-aurora v2-aurora--green opacity-25" />
        </div>
        <MenuHeading
          eyebrow="Friends"
          title="Your Friends."
          greenSuffix="Synced."
          data-testid="heading-friends"
        />
      </section>

      {/* Trainer card (no PoGo friend code, tap-to-reveal only on owner profile).
          mt-1, not the mt-5 block gap: the heading's own line-height already
          contributes air, and the first block should sit as close to the
          heading as the later blocks sit to each other (2026-08-21 ask). */}
      <div className="mt-1" data-dotrail-dot>
        <TrainerCard onShowQr={() => setQrOpen(true)} onAdd={() => setAddOpen(true)} />
      </div>

      {/* wish-list-screen: the trainer's own Wish List, directly under their dex
          summary. Pro-gated — Basic users see a tease that upsells.

          The `data-dotrail-dot` MUST live here, on a wrapper, not inside
          MyWishlistSection: DotRail reads the JSX child's props
          (hasMarker -> el.props[marker]), so a marker on the <section> a
          component renders is invisible to it and the block silently loses its
          rail dot. Same wrapper pattern as TrainerCard above. */}
      <div data-dotrail-dot>
        <MyWishlistSection />
      </div>

      {/* The "Best for your trophies" carousel stood here until 2026-08-14.
          It was sourced from pinned CUSTOM dexes and scored against a friends
          array hardcoded to `[]` ("pending live source"), so it could never
          produce a card and has never rendered for anybody. It went with the
          custom-dex builder and its invented meta lists; the real rankings now
          live in the Battle Hub. */}

      {/* Pending invites and live friends from the server. PendingInvitesSection
          renders null when there are no invites, so it is intentionally NOT
          tagged for a rail dot (an opt-in dot on it would float in an empty gap
          when the section is absent). LiveFriendsSection always renders its
          titled "Your friends" block (incl. its empty state), so it carries a
          dot. */}
      <PendingInvitesSection onOpenAdd={() => setAddOpen(true)} />
      <div data-dotrail-dot>
        <LiveFriendsSection onOpenAdd={() => setAddOpen(true)} />
      </div>

      {/* v1.1.2-b5 SURFACE 9: full friends matrix view. Basic users see the
          name list above. Pro unlocks the cross-trainer dex-completion matrix.
          Wrapped for the rail dot: DotRail reads JSX props, so a marker inside
          FriendsMatrixProSection would be invisible (see MyWishlistSection). */}
      <div data-dotrail-dot>
        <FriendsMatrixProSection />
      </div>


      {/* Trade preview list, sourced from mock fixtures for design QA.
          In prod we suppress demo trainers entirely; LiveFriendsSection above
          shows the real connected friends. */}
      {!import.meta.env.PROD && (
        <section className="mt-7" data-dotrail-dot>
          <div className="flex items-center justify-between mb-3">
            <span className="v2-eyebrow" data-testid="eyebrow-network">
              <span className="v2-eyebrow__dot" aria-hidden />
              Trainers in your network
            </span>
            <span className="text-ink-muted text-[9px] uppercase tracking-wider">
              Preview
            </span>
          </div>
          <div className="space-y-3">
            {/* b19: pending live source (see b19_backlog.md B19-2 PR #3+) */}
            {([] as Friend[]).map((f) =>
              f.presenceVisible === false ? (
                <PrivateFriendRow key={f.id} friend={f} />
              ) : (
                <FriendRow
                  key={f.id}
                  friend={f}
                  expanded={expandedId === f.id}
                  onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)}
                  onViewDex={() => setViewingFriend(f.id)}
                  onCompare={() => setLocation(`/friends/${f.id}/compare`)}
                  onTrades={() => setLocation(`/friends/${f.id}/trades`)}
                  onShareMissing={(numbers, label) => openShare({ numbers, label })}
                />
              ),
            )}
          </div>
        </section>
      )}

      </DotRail>
      {addOpen && <AddFriendModal onClose={() => setAddOpen(false)} onShowQr={() => { setAddOpen(false); setQrOpen(true); }} />}
      {qrOpen && <QrCodeModal onClose={() => setQrOpen(false)} />}
    </AppShell>
  );
}

/* ════════════════════════════════════════════════════════════════════
   wish-list-screen: the trainer's own Wish List, shown under their dex
   summary on the Friends page. Pro-gated — Basic users get a tease that
   opens the paywall; Pro users see their seven chased copies with a link
   into the full Wish List editor.
   ════════════════════════════════════════════════════════════════════ */
function MyWishlistSection() {
  const { tier } = useSession();
  const isPro = hasProAccess(tier);
  const wishlist = useWishlist();
  const [, setLocation] = useLocation();

  // No data-dotrail-dot on the <section> below: DotRail inspects the JSX child's
  // props, so a marker on the DOM a component renders is never seen. The call
  // site wraps this component in a marked <div> instead.
  return (
    <section className="mt-7" data-testid="section-my-wishlist">
      <div className="flex items-center justify-between mb-3">
        <span className="v2-eyebrow" data-testid="eyebrow-my-wishlist">
          <span className="v2-eyebrow__dot" aria-hidden />
          Your wish list
        </span>
        {isPro && (
          <button
            type="button"
            onClick={() => setLocation("/wish-list")}
            data-testid="my-wishlist-manage"
            className="inline-flex items-center gap-0.5 text-[11px] font-semibold hover-elevate active-elevate-2 rounded-full px-2 py-1"
            style={{ color: "var(--brand-green-on-light)" }}
          >
            {wishlist.length > 0 ? "Manage" : "Add"}
            <ChevronRight className="w-3.5 h-3.5" aria-hidden />
          </button>
        )}
      </div>

      {isPro ? (
        <button
          type="button"
          onClick={() => setLocation("/wish-list")}
          data-testid="my-wishlist-open"
          className="w-full text-left v2-neon-card-soft p-4 hover-elevate active-elevate-2"
        >
          <WishlistStrip
            entries={wishlist}
            showHeader={false}
            layout="grid"
            emptyLabel="You're not chasing anything yet. Tap to pin up to seven Pokémon you're hunting so friends can help."
          />
        </button>
      ) : (
        <ProLock
          feature="wish-list"
          source="wish-list"
          blurb="Show friends what you're chasing. Pin up to seven Pokémon you're hunting and they'll show on every friend's compare page so they can help you find them. Included with Pro."
        />
      )}
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Trainer Card, PoGo friend code REMOVED per spec
   ════════════════════════════════════════════════════════════════════ */
function TrainerCard({ onShowQr, onAdd }: { onShowQr: () => void; onAdd: () => void }) {
  const { account } = useAuth();
  const [, setLocation] = useLocation();
  // PoGo identity only. Never the IdP displayName.
  const handle = trainerHandle(account) ?? "@trainer";

  // Wire to real backend. /api/friends returns { friends }.
  const friendsQ = useQuery<{ friends: unknown[] }>({
    queryKey: ["/api/friends"],
  });

  // b19: pending live source (see b19_backlog.md B19-2 PR #3+)
  const friendCount = friendsQ.data?.friends?.length ?? 0;

  // b17s S4: the self-trainer counts MUST match the Profile page and Dex menu.
  // Those read the device-local dexStateStore via deriveDexCounts (distinct
  // CAUGHT species per variant, cascade-composed for Main). The old source here
  // was /api/dex/batches stats.total, which is uploaded sightings/rows, not
  // distinct species, so it over-counted (e.g. Main 1089 vs a 1025-species dex)
  // and that bogus number also leaked into Compare. Reading deriveDexCounts ties
  // this card to the exact same source of truth Profile uses. countsTick re-reads
  // the store on every dex-state / dex-totals event (deriveDexCounts is not a
  // React value itself), mirroring Profile's refresh wiring.
  const [countsTick, setCountsTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setCountsTick((n) => n + 1);
    window.addEventListener(DEX_TOTALS_EVENT, handler);
    window.addEventListener(DEX_STATE_EVENT, handler);
    return () => {
      window.removeEventListener(DEX_TOTALS_EVENT, handler);
      window.removeEventListener(DEX_STATE_EVENT, handler);
    };
  }, []);
  const completion = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => deriveDexCounts(),
    [countsTick],
  );
  const cats: { key: keyof typeof completion; label: string; color: string; track: string }[] = [
    { key: "main",    label: "Main",    color: "hsl(var(--primary))", track: "hsl(var(--primary) / 0.18)" },
    { key: "shiny",   label: "Shiny",   color: "hsl(var(--shiny))",   track: "hsl(var(--shiny) / 0.18)" },
    { key: "lucky",   label: "Lucky",   color: "hsl(var(--lucky))",   track: "hsl(var(--lucky) / 0.18)" },
    { key: "perfect", label: "Perfect", color: "hsl(var(--perfect))", track: "hsl(var(--perfect) / 0.18)" },
  ];

  return (
    <section className="bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] relative overflow-hidden p-5" data-testid="card-trainer">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="v2-aurora v2-aurora--green opacity-30" />
      </div>

      <div className="relative flex items-start gap-4">
        <Monogram letter={trainerInitial(account)} hue={142} size={64} />
        <div className="min-w-0 flex-1">
          <p className="text-ink-accent text-[10px] uppercase tracking-[0.22em] font-bold">
            Trainer
          </p>
          <p className="text-ink-strong text-lg font-bold mt-0.5" data-testid="text-trainer-handle">
            {handle}
          </p>
          <p className="text-ink-faint text-[11px] mt-0.5">
            {friendCount} friend{friendCount === 1 ? "" : "s"}
          </p>
        </div>
        <IndexedKpiPill className="shrink-0" showLabel={false} />
      </div>

      <div className="relative grid grid-cols-4 gap-2 mt-5">
        {cats.map((c) => {
          const pct = dexPercent(completion[c.key], TOTAL_RELEASED);
          return (
            <button
              key={c.key}
              type="button"
              // PR-D #7: each stat tile deep-links to the Dex tab with the
              // matching variant pre-selected (Pokedex reads ?filter on mount).
              onClick={() => setLocation(`/dex?filter=${c.key}`)}
              data-testid={`trainer-stat-${c.key}`}
              aria-label={`Open ${c.label} Dex`}
              className="surface-trainer-tile rounded-xl p-2.5 text-left hover-elevate active-elevate-2"
            >
              <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: c.color }}>{c.label}</p>
              <p className="text-ink-strong font-mono text-sm font-bold tabular-nums mt-1">
                {completion[c.key]}
              </p>
              <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: c.track }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c.color }} />
              </div>
              <p className="text-ink-muted font-mono text-[9px] mt-1">{pct}%</p>
            </button>
          );
        })}
      </div>

      <div className="relative mt-5 grid grid-cols-2 gap-2">
        {/* Viral engine: Add a friend leads (left, dark) — it's the referral
            loop's entry point; My QR keeps the bright brand fill on the right. */}
        <button
          data-testid="button-add-a-friend"
          onClick={onAdd}
          className="btn-fit surface-dark text-ink-bright rounded-2xl py-3 px-4 inline-flex flex-col items-center justify-center gap-0.5 text-sm font-bold uppercase tracking-wider transition"
        >
          <UserPlus className="w-4 h-4" />
          <span className="btn-fit__label">Add a friend</span>
        </button>
        <button
          data-testid="button-show-my-qr"
          onClick={onShowQr}
          // b17s S3: in light mode bg-[var(--accent-fill)] is the tamed #0DA371, which
          // read as a pale, low-contrast fill. Match NexButton's primary CTA
          // treatment instead: the deep AA-legible --brand-green-700 with white
          // text in light mode, flipping to the neon --accent in dark mode. This
          // restores the loud brand-green look without touching the shared token.
          // Light ink is FIXED white, not --text-on-accent: that token is tuned
          // for the bright accent fill, and the faction accents flip it to a
          // dark ink (Instinct: #3B2F00) — dark-on-deep-gold read as a muddy
          // brown button (device report 2026-08-27). White passes AA on every
          // faction's deep --brand-green-700 shade.
          className="btn-fit py-3 px-4 inline-flex flex-col items-center justify-center gap-0.5 rounded-[var(--radius-lg)] text-sm bg-[var(--brand-green-700)] text-white font-semibold dark:bg-[var(--accent-fill)] dark:text-[var(--text-on-accent)]"
        >
          <QrCode className="w-4 h-4" />
          <span className="btn-fit__label">My QR code</span>
        </button>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Friend Row, expandable, per-dex "Copy missing numbers" actions
   ════════════════════════════════════════════════════════════════════ */
function FriendRow({
  friend, expanded, onToggle, onViewDex, onCompare, onTrades, onShareMissing,
}: {
  friend: Friend;
  expanded: boolean;
  onToggle: () => void;
  onViewDex: () => void;
  onCompare: () => void;
  onTrades: () => void;
  onShareMissing: (numbers: string, label: string) => void;
}) {
  const { toast } = useToast();
  const cats: { key: keyof typeof friend.progress; label: string; full: string; color: string; track: string }[] = [
    { key: "main",    label: "M", full: "Main dex",    color: "hsl(var(--primary))", track: "hsl(var(--primary) / 0.15)" },
    { key: "shiny",   label: "S", full: "Shiny dex",   color: "hsl(var(--shiny))",   track: "hsl(var(--shiny) / 0.15)" },
    { key: "lucky",   label: "L", full: "Lucky dex",   color: "hsl(var(--lucky))",   track: "hsl(var(--lucky) / 0.15)" },
    { key: "perfect", label: "P", full: "Perfect dex", color: "hsl(var(--perfect))", track: "hsl(var(--perfect) / 0.15)" },
  ];

  // Compute THEIR missing numbers for each dex (deterministic demo)
  const missingByCat = useMemo(() => {
    const out: Record<string, string> = {};
    (["main", "shiny", "lucky", "perfect"] as const).forEach((cat) => {
      // Use friend hue as seed variation, pick ~8-12 missing ids
      // b19: pending live source (see b19_backlog.md B19-2 PR #3+)
      const dex: PokemonDexEntry[] = [];
      const ids = dex
        .filter((e) => e.status === "missing")
        .map((e) => e.pokemon.id)
        .filter((_, i) => (i + friend.hue) % 3 === 0)
        .slice(0, 10);
      out[cat] = ids.join(", ");
    });
    return out;
  }, [friend.hue]);

  function copyMissing(cat: keyof typeof friend.progress, label: string) {
    const nums = missingByCat[cat as string];
    if (!nums) return;
    navigator.clipboard.writeText(nums);
    toast({ title: `Copied numbers they need for ${label}`, description: `${nums.split(",").length} Pokémon numbers copied.` });
  }

  return (
    <div
      className="bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] overflow-hidden"
      data-testid={`friend-card-${friend.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 transition hover:brightness-110"
      >
        <div className="flex items-start gap-3">
          <Monogram letter={friend.monogram} hue={friend.hue} size={48} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-ink-strong text-sm font-bold">{friend.handle}</p>
              <span className="text-ink-faint text-[10px]">· {friend.lastActive}</span>
              {friend.matchCount && (
                <span
                  className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: "var(--accent-subtle)", color: "var(--accent-fill)", border: "1px solid var(--border-subtle)" }}
                >
                  {friend.matchCount} matches
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {cats.map((c) => {
                const v = friend.progress[c.key] ?? 0;
                const pct = dexPercent(v, TOTAL_RELEASED);
                return (
                  <div key={c.key} className="flex items-center gap-1.5">
                    <span className="text-[9px] font-bold w-3 shrink-0" style={{ color: c.color }}>{c.label}</span>
                    <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: c.track }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {expanded
            ? <ChevronUp   className="text-ink-accent w-4 h-4 shrink-0 mt-1" />
            : <ChevronDown className="text-ink-accent w-4 h-4 shrink-0 mt-1" />
          }
        </div>
      </button>

      {expanded && (
        <div className="px-4 pt-3 pb-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <p className="text-ink-accent text-[10px] font-bold uppercase tracking-[0.18em] mb-2.5">
            Help them finish a dex
          </p>
          <div className="space-y-2">
            {cats.map((c) => {
              const missingCount = missingByCat[c.key as string].split(", ").filter(Boolean).length;
              return (
                <div
                  key={c.key}
                  className="surface-trainer-tile flex items-center gap-2 rounded-xl p-2.5"
                >
                  <span
                    className="w-7 h-7 rounded-lg text-[10px] font-bold flex items-center justify-center shrink-0"
                    style={{ background: c.track, color: c.color, border: `1px solid ${c.color.replace(")", " / 0.35)")}` }}
                  >
                    {c.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold">{c.full}</p>
                    <p className="text-[10px] text-muted-foreground">{missingCount} numbers they need</p>
                  </div>
                  <button
                    onClick={() => copyMissing(c.key, c.full)}
                    data-testid={`button-copy-missing-${friend.id}-${c.key}`}
                    className="h-7 rounded-full px-2.5 text-[11px] font-semibold inline-flex items-center gap-1 hover-elevate"
                    style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                  <button
                    onClick={() => onShareMissing(missingByCat[c.key as string], `${friend.displayName}'s missing ${c.full.toLowerCase()}`)}
                    data-testid={`button-share-missing-${friend.id}-${c.key}`}
                    className="h-7 rounded-full px-2.5 text-[11px] font-semibold inline-flex items-center gap-1 hover-elevate"
                    style={{ background: "var(--accent-subtle)", color: "var(--accent-fill)", border: "1px solid var(--border-subtle)" }}
                  >
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                </div>
              );
            })}
          </div>

          {/* The same VS / trade pair the live rows carry, so the two rows
              can't drift apart. See FriendRowAction. */}
          <div className="mt-3 flex items-center gap-2">
            <FriendRowAction
              label={`Compare Dex with ${friend.displayName}`}
              testId={`button-compare-${friend.id}`}
              tone="teal"
              onClick={onCompare}
            >
              <span className="text-[10px] font-extrabold tracking-tight leading-none">VS</span>
            </FriendRowAction>
            <FriendRowAction
              label={`Open trades with ${friend.displayName}`}
              testId={`button-trades-${friend.id}`}
              tone="accent"
              onClick={onTrades}
            >
              <TradeSwapIcon className="w-4 h-4" strokeWidth={2} />
            </FriendRowAction>
            <button
              onClick={onViewDex}
              data-testid={`button-view-dex-${friend.id}`}
              className="flex-1 rounded-xl py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover-elevate"
              style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
            >
              View full dex <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Private Friend Row, shown when friend has presence hidden
   ════════════════════════════════════════════════════════════════════ */
function PrivateFriendRow({ friend }: { friend: Friend }) {
  return (
    <div
      data-testid={`row-friend-private-${friend.id}`}
      className="rounded-2xl px-4 py-3.5 flex items-center gap-3"
      style={{
        background: "hsl(var(--card) / 0.5)",
        border: "1.5px dashed var(--surface-elev-border)",
      }}
    >
      <div className="relative shrink-0">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center text-base font-bold"
          style={{
            background: `hsl(${friend.hue} 30% 35% / 0.4)`,
            color: `hsl(${friend.hue} 25% 70%)`,
            filter: "saturate(0.5)",
          }}
        >
          {firstInitial(friend.handle.replace("@", ""), "T")}
        </div>
        <div
          className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
          style={{
            background: "hsl(var(--card))",
            border: "1.5px solid var(--surface-elev-border)",
          }}
          aria-label="Private"
        >
          <Lock className="w-2.5 h-2.5 text-muted-foreground" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold truncate text-muted-foreground">{friend.handle}</p>
          <span
            className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{
              background: "color-mix(in srgb, var(--surface-elev-border) 50%, transparent)",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            Private
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground/80 mt-0.5 leading-snug">
          Stats hidden until they enable presence.
        </p>
      </div>
    </div>
  );
}

function FriendDexView({ friendId, onBack, onGenerate }: {
  friendId: string;
  onBack: () => void;
  onGenerate: (cat: DexCategory) => void;
}) {
  // b19: pending live source (see b19_backlog.md B19-2 PR #3+)
  const friend = ([] as Friend[]).find((f) => f.id === friendId);
  if (!friend) return null;
  const sampleIds = [25, 6, 9, 94, 130, 143, 144, 145, 149, 150, 248, 282, 359, 445, 448, 487, 700, 785, 786, 787, 888, 959, 887, 798, 894];
  const statuses: ("caught" | "missing" | "seen")[] = sampleIds.map((_, i) => {
    const v = (i * 13 + friend.hue) % 10;
    return v < 6 ? "caught" : v < 8 ? "seen" : "missing";
  });

  const cats: { key: DexCategory; label: string; color: string }[] = [
    { key: "main",    label: "Main",    color: "hsl(var(--primary))" },
    { key: "shiny",   label: "Shiny",   color: "hsl(var(--shiny))" },
    { key: "lucky",   label: "Lucky",   color: "hsl(var(--lucky))" },
    { key: "perfect", label: "Perfect", color: "hsl(var(--perfect))" },
  ];

  return (
    <div className="space-y-4">
      <button
        data-testid="button-back-friends"
        onClick={onBack}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 -ml-1"
      >
        <ChevronLeft className="w-4 h-4" /> Back to friends
      </button>

      <div className="rounded-2xl p-5 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, hsl(${friend.hue} 60% 38% / 0.38), var(--surface-elev-1) 75%)`,
          border: `1px solid hsl(${friend.hue} 60% 50% / 0.3)`,
        }}>
        <div className="flex items-center gap-3">
          <Monogram letter={friend.monogram} hue={friend.hue} size={56} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Viewing trainer</p>
            <p className="text-lg font-bold">{friend.handle}</p>
            <p className="text-[11px] text-muted-foreground">Connected via Nexus Dex · last active {friend.lastActive}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2.5">
        {cats.map((cat) => {
          const v = friend.progress[cat.key] ?? 0;
          const pct = dexPercent(v, TOTAL_RELEASED);
          return (
            <div key={cat.key} className="surface-elev rounded-xl p-3 text-center">
              <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: cat.color }}>{cat.label}</p>
              <p className="font-mono text-base font-bold tabular-nums mt-0.5">{v}</p>
              <p className="text-[9px] text-muted-foreground">{pct}%</p>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl p-4 surface-elev">
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
          Generate a trade string for their missing dex
        </p>
        <div className="grid grid-cols-2 gap-2">
          {cats.map((cat) => (
            <button
              key={cat.key}
              data-testid={`button-generate-for-${cat.key}`}
              onClick={() => onGenerate(cat.key)}
              className="rounded-xl py-2.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover-elevate"
              style={{
                background: "var(--surface-elev-2)",
                border: `1px solid ${cat.color.replace(")", " / 0.4)")}`,
                color: cat.color,
              }}
            >
              <Wand2 className="w-3.5 h-3.5" /> {cat.label} dex
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
          Sample of their main dex
        </p>
        <div className="grid grid-cols-5 sm:grid-cols-7 gap-2.5 dex-grid-bg rounded-2xl p-3 surface-elev">
          {sampleIds.map((id, i) => (
            <div key={id} className="aspect-square flex flex-col items-center justify-center gap-0.5 rounded-lg p-1.5">
              <PokemonSprite id={id} status={statuses[i]} category="main" size={42} />
              <span className="font-mono text-[9px] text-muted-foreground/70">{dexNumberLabel(id)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Free user teaser
   ════════════════════════════════════════════════════════════════════ */
function FreeFriendsTeaser({ onUnlock, onShowQr }: { onUnlock: () => void; onShowQr: () => void }) {
  const { prices } = usePrices();
  return (
    <div className="space-y-5">
      <TrainerCard onShowQr={onShowQr} onAdd={onUnlock} />
      <div
        className="rounded-3xl p-6 text-center"
        style={{
          background: "linear-gradient(135deg, var(--teal-fill-subtle), var(--surface-elev-1) 70%)",
          border: "1px dashed var(--teal-border-subtle)",
        }}
      >
        <p className="text-sm font-semibold">Connect with your first friend</p>
        <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
          Friends get 1 free full search workflow. You unlock dex comparison and Best Trades.
        </p>
        <Button
          onClick={onUnlock}
          className="mt-4 rounded-full font-semibold"
          style={{ background: "var(--cta-gradient)", color: "white" }}
        >
          Get Pro · {formatPlanLine(prices.monthly, "/mo")} or {formatPlanLine(prices.annual, "/yr")}
        </Button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   QR Code Modal (show YOUR QR for friends to scan)
   ════════════════════════════════════════════════════════════════════ */
function QrCodeModal({ onClose }: { onClose: () => void }) {
  const { account } = useAuth();
  // PoGo identity only. Never the IdP displayName.
  const resolvedHandle = trainerHandle(account);
  const handle = resolvedHandle ?? "@trainer";
  const { toast } = useToast();

  // b14: mint a real HMAC-signed add-friend token. The QR encodes the
  // nexusdex://add-friend deep link so a scan auto-creates the request.
  type QrTokenResp = { token: string; handle: string; expiresAt: number; deepLink: string };
  const tokenQuery = useQuery<QrTokenResp>({
    queryKey: ["/api/friends/qr-token"],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/friends/qr-token", {});
      return res.json();
    },
    staleTime: 60 * 60 * 1000, // token is valid 24h; refresh hourly at most
    retry: false,
  });

  const deepLink = tokenQuery.data?.deepLink ?? null;
  // "Add your trainer name first" must key off the actual identity state, not
  // a transient token-mint failure. A trainer with a name should always get
  // their QR (or a retry), never the prompt. The handle comes with the trainer
  // name (the confirm derives it); there is nowhere to claim one by itself.
  const noHandle = resolvedHandle == null;

  function copyLink() {
    if (!deepLink) return;
    navigator.clipboard.writeText(deepLink);
    toast({ title: "Invite link copied", description: `@${handle.replace(/^@/, "")}` });
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm anim-fade-up"
      onClick={onClose}
      role="dialog"
      aria-label="Your Nexus Dex QR code"
    >
      <div
        className="w-full md:max-w-md glass-strong rounded-t-3xl md:rounded-3xl p-6 max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold tracking-tight">Your QR code</h3>
            <p className="text-xs text-muted-foreground mt-1">Ask a friend to scan this with Nexus Dex.</p>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-qr"
            className="w-8 h-8 rounded-full hover-elevate flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <div
            className="rounded-2xl p-4 relative flex items-center justify-center"
            style={{ background: "white", border: "1px solid var(--surface-elev-border)", width: 252, height: 252 }}
          >
            {tokenQuery.isLoading ? (
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#0B0E1A" }} />
            ) : noHandle ? (
              <div className="text-center px-4">
                <AtSign className="w-7 h-7 mx-auto mb-2" style={{ color: "#0B0E1A" }} />
                <p className="text-xs font-semibold" style={{ color: "#0B0E1A" }}>
                  Add your trainer name first
                </p>
                <button
                  onClick={() => {
                    // Opens the trainer step (ProfileGate) over the app; this
                    // sheet closes so it is not left sitting on top of it.
                    requestProfileSetup(account?.id);
                    onClose();
                  }}
                  data-testid="button-qr-add-trainer-name"
                  className="mt-2 text-[11px] font-semibold underline underline-offset-2"
                  style={{ color: "#0B0E1A" }}
                >
                  Add my trainer name
                </button>
              </div>
            ) : !deepLink ? (
              <div className="text-center px-4">
                <AtSign className="w-7 h-7 mx-auto mb-2" style={{ color: "#0B0E1A" }} />
                <p className="text-xs font-semibold" style={{ color: "#0B0E1A" }}>
                  Could not generate your code
                </p>
                <button
                  onClick={() => tokenQuery.refetch()}
                  className="mt-2 text-[11px] font-semibold underline underline-offset-2"
                  style={{ color: "#0B0E1A" }}
                >
                  Try again
                </button>
              </div>
            ) : (
              <QRCodeSVG
                value={deepLink}
                size={220}
                bgColor="#ffffff"
                fgColor="#0B0E1A"
                level="M"
              />
            )}
          </div>
          <p className="mt-3 text-sm font-semibold">{handle}</p>
          <p className="text-[11px] text-muted-foreground text-center px-6 leading-relaxed">
            {noHandle
              ? "Your QR code is made from your Pokémon GO trainer name."
              : !deepLink
                ? "We couldn't reach the server to mint your invite. Tap Try again."
                : "Scanning this auto-sends them a friend request. Link expires in 24h."}
          </p>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <Button
            onClick={copyLink}
            disabled={!deepLink}
            data-testid="button-copy-invite-link"
            variant="secondary"
            className="rounded-full"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
          </Button>
          <Button
            data-testid="button-share-invite"
            onClick={copyLink}
            disabled={!deepLink}
            className="rounded-full"
            style={{ background: "var(--cta-gradient)", color: "white" }}
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> Share
          </Button>
        </div>

        <p className="mt-4 text-[11px] text-muted-foreground/80 leading-relaxed text-center">
          No Pokémon GO trainer code needed. Friends connect inside Nexus Dex.
        </p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Add Friend Modal, Scan QR / Enter code tabs.
   v10: "Send link" tab and trial-invite system removed entirely. Friends
   join Nexus Dex via their own subscription or the in-app trial.
   ════════════════════════════════════════════════════════════════════ */
function AddFriendModal({ onClose, onShowQr }: { onClose: () => void; onShowQr: () => void }) {
  const { toast } = useToast();
  const { openPaywall, refreshEntitlements } = useSession();
  // B11 (v1.0.10-b2): the Referral tab was removed alongside the referral
  // system. The other three tabs still drive normal friend addition.
  // Viral engine: Code leads (and is the default) — it's the referral loop's
  // entry point (the shared invite link carries a code), so it comes first.
  const [tab, setTab] = useState<"scan" | "handle" | "code">("code");
  const [codeInput, setCodeInput] = useState("");
  const [handleInput, setHandleInput] = useState("");

  // Username-addressed pending request. Distinct from /redeem (which is short-
  // code based and auto-accepts). This path produces a real pending row the
  // recipient can accept or decline from their Friends page.
  const inviteByUsernameMutation = useMutation<
    { ok: boolean; alreadyFriends?: boolean },
    Error,
    { username: string }
  >({
    mutationFn: async (payload) => {
      const res = await apiRequest("POST", "/api/friends/invite-by-username", payload);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
      toast({
        title: data.alreadyFriends ? "Already friends" : "Request sent",
        description: data.alreadyFriends
          ? "You're already connected with that trainer."
          : "They'll see it in their pending invites.",
      });
      onClose();
    },
    onError: (err) => {
      // Basic hit the outstanding-pending cap (one request at a time): surface
      // the paywall instead of a dead-end toast, matching redeem/accept.
      if (isFriendLimitError(err)) {
        openPaywall("friends-error", errorMessage(err));
        return;
      }
      toast({ title: "Couldn't send request", description: errorMessage(err), variant: "destructive" });
    },
  });

  // Pull the user's own invite code (lazy create on first call) and a redeem mutation.
  type InviteResp = { code: string; url: string; redeemCount: number; referralCount: number };
  const inviteQuery = useQuery<InviteResp>({
    queryKey: ["/api/friends/invite"],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/friends/invite", {});
      return res.json();
    },
  });
  type RedeemResp = {
    ok: true;
    storageGranted: boolean;
    isReferral: boolean;
    proGranted?: boolean;
    proDays?: number;
  };
  const redeemMutation = useMutation<RedeemResp, Error, { code: string; isBrandNewSignup: boolean }>({
    mutationFn: async (payload) => {
      const res = await apiRequest("POST", "/api/friends/redeem", payload);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends/referrals"] });
      // Viral engine: a verified referral can land a 7-day Pro grant on THIS
      // account (the redeemer side). Re-pull entitlements so the gates unlock
      // now, not on the next cold start.
      if (data.proGranted) void refreshEntitlements();
      const desc = data.proGranted
        ? `You joined from a friend's invite: ${data.proDays ?? 7} days of Nexus Dex Pro are now unlocked.`
        : data.storageGranted
          ? "And you just unlocked an extra storage slot from referrals."
          : data.isReferral
            ? "They counted as a referral, keep going."
            : "You'll see them in your friends list shortly.";
      toast({ title: data.proGranted ? "Friend added: Pro unlocked" : "Friend added", description: desc });
      onClose();
    },
    onError: (err: Error) => {
      // At the Basic friend cap: sell the upgrade instead of surfacing a 403.
      // openPaywall takes the sentence shown under the heading, and the server
      // already sends one — keep that copy in a single place.
      if (isFriendLimitError(err)) {
        openPaywall("friends-error", errorMessage(err));
        return;
      }
      toast({ title: "Couldn't redeem", description: errorMessage(err), variant: "destructive" });
    },
  });
  const myCode = inviteQuery.data?.code ?? YOUR_CONNECT_URL.split("/").pop()!;

  return (
    // Centred, not docked to the bottom (2026-09-15 owner ask): the soft
    // keyboard overlays the page (Keyboard.resize none), so a bottom sheet
    // put the handle box under the keys. The overlay pads its bottom by the
    // keyboard's height (--keyboard-h from GlobalKeyboard) so the sheet is
    // centred in the space that is left ABOVE the keyboard while typing.
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm anim-fade-up px-4"
      style={{ paddingBottom: "var(--keyboard-h, 0px)", transition: "padding-bottom 160ms ease-out" }}
      onClick={onClose}
      role="dialog"
      aria-label="Add a friend"
      data-testid="addfriend-overlay"
    >
      <div
        className="w-full max-w-md glass-strong rounded-3xl p-6 overflow-y-auto"
        style={{ maxHeight: "calc(100dvh - var(--keyboard-h, 0px) - 2rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold tracking-tight">Add a friend</h3>
            <p className="text-xs text-muted-foreground mt-1">Nexus Dex uses its own invite codes. You can also send a request by their Pokémon GO trainer name.</p>
          </div>
          <button onClick={onClose} data-testid="button-close-addfriend" className="w-8 h-8 rounded-full hover-elevate flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-1 mb-4 rounded-xl p-1" style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}>
          {[
            { key: "code",     label: "Code",     icon: <KeyRound className="w-3.5 h-3.5" /> },
            { key: "handle",   label: "Name",     icon: <AtSign   className="w-3.5 h-3.5" /> },
            { key: "scan",     label: "QR",       icon: <ScanLine className="w-3.5 h-3.5" /> },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-add-${t.key}`}
                onClick={() => setTab(t.key as typeof tab)}
                className={`py-2 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1 transition ${
                  active ? "text-foreground" : "text-muted-foreground"
                }`}
                style={active ? { background: "hsl(var(--primary) / 0.16)", border: "1px solid hsl(var(--primary) / 0.4)" } : {}}
              >
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>

        {/* Name tab, the canonical "send an invite" path. The friend's Pokémon
            GO trainer name; the server tries it as a handle first and then as
            the name itself (worker lib/friendNameLookup). */}
        {tab === "handle" && (
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Their trainer name
            </label>
            <div
              className="mt-1.5 flex items-center rounded-xl px-3 py-3"
              style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
            >
              <AtSign className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                value={handleInput}
                onChange={(e) => setHandleInput(e.target.value.replace(/^@+/, ""))}
                placeholder="Their Pokémon GO trainer name"
                data-testid="input-friend-handle"
                className="ml-2 bg-transparent w-full text-sm focus:outline-none"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <Button
              onClick={() => {
                // No spaces in a trainer name: one typed or pasted in is dropped.
                const username = handleInput.replace(/\s+/g, "").replace(/^@/, "");
                if (username.length < 3) {
                  toast({ title: "Name too short", description: "Enter their Pokémon GO trainer name." });
                  return;
                }
                inviteByUsernameMutation.mutate({ username });
              }}
              disabled={inviteByUsernameMutation.isPending}
              data-testid="button-submit-friend-handle"
              className="w-full mt-3 rounded-full py-2.5 text-xs font-semibold hover-elevate"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
            >
              {inviteByUsernameMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send friend request</>
              )}
            </Button>
            <p className="mt-3 text-[11px] text-muted-foreground/80 leading-relaxed">
              They'll see your request in their Pending invites. They can accept or decline.
            </p>
          </div>
        )}

        {/* Scan tab */}
        {tab === "scan" && <QrScanTab onShowQr={onShowQr} />}

        {/* Code tab */}
        {tab === "code" && (
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Nexus Dex friend code</label>
            <input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="e.g. X9P3-K2A8"
              data-testid="input-friend-code"
              className="mt-1.5 w-full rounded-xl px-3 py-3 font-mono tracking-wider text-sm"
              style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)", color: "hsl(var(--foreground))" }}
            />
            <Button
              onClick={() => {
                if (codeInput.length < 4) {
                  toast({ title: "Code too short", description: "Double-check the 8-character code." });
                  return;
                }
                redeemMutation.mutate({ code: codeInput, isBrandNewSignup: false });
              }}
              disabled={redeemMutation.isPending}
              data-testid="button-submit-friend-code"
              className="w-full mt-3 rounded-full py-2.5 text-xs font-semibold hover-elevate"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
            >
              {redeemMutation.isPending ? "Sending\u2026" : "Send friend request"}
            </Button>
            <div
              className="mt-4 rounded-xl p-3"
              style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)" }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Your invite link
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground/80 leading-relaxed">
                Your code is <span className="font-mono text-foreground">{myCode}</span>. When 2
                friends join from your link and finish their first scan, you all get 7 days of Pro
                free{typeof inviteQuery.data?.referralCount === "number" && inviteQuery.data.referralCount > 0
                  ? `, ${inviteQuery.data.referralCount} joined so far`
                  : ""}.
              </p>
            </div>
            {/* Outside the padded box so it spans the same full width as the
                Send friend request button above. */}
            <Button
              onClick={() => {
                const url = inviteQuery.data?.url;
                if (!url) {
                  toast({ title: "Invite not ready", description: "Try again in a second." });
                  return;
                }
                void (async () => {
                  const { shareInviteUrl } = await import("@/lib/inviteShare");
                  const outcome = await shareInviteUrl(url);
                  if (outcome === "copied") {
                    toast({ title: "Invite copied", description: "Your invite link is on the clipboard." });
                  }
                })();
              }}
              data-testid="button-share-invite-link"
              className="w-full mt-2.5 rounded-full py-2.5 text-xs font-semibold hover-elevate"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
            >
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share invite link
            </Button>
          </div>
        )}

        {/* B11 (v1.0.10-b2): the Referral tab here was removed along with
            the rest of the referral system. Trainers can still add friends
            via handle, QR, or code, just not via a referral chain. */}

      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Pending invites section, live data
   ════════════════════════════════════════════════════════════════════ */
function PendingInvitesSection({ onOpenAdd: _onOpenAdd }: { onOpenAdd: () => void }) {
  const { toast } = useToast();
  const { openPaywall } = useSession();
  type PendingFriend = {
    id: number;
    createdAt: number | string;
    friend: { id: number; username: string | null; displayName: string | null; trainerName: string | null } | null;
  };
  const pendingQuery = useQuery<{ pending: PendingFriend[] }>({
    queryKey: ["/api/friends/pending"],
  });

  const acceptMutation = useMutation<unknown, Error, number>({
    mutationFn: async (id) => {
      const res = await apiRequest("POST", `/api/friends/${id}/accept`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
      toast({ title: "Friend added", description: "You're now connected." });
    },
    onError: (err) => {
      if (isFriendLimitError(err)) {
        openPaywall("friends-error", errorMessage(err));
        return;
      }
      toast({ title: "Couldn't accept", description: errorMessage(err), variant: "destructive" });
    },
  });

  const declineMutation = useMutation<unknown, Error, number>({
    mutationFn: async (id) => {
      const res = await apiRequest("POST", `/api/friends/${id}/decline`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
      toast({ title: "Request declined" });
    },
    onError: (err) => toast({ title: "Couldn't decline", description: err.message, variant: "destructive" }),
  });

  const pending = pendingQuery.data?.pending ?? [];
  // Hide the section entirely when there's nothing to act on. Loading state
  // is silent on first render, the section pops in once data arrives.
  if (pendingQuery.isLoading || pending.length === 0) return null;

  return (
    <section className="mt-7" data-testid="section-pending-invites">
      <div className="flex items-center justify-between mb-3">
        <span className="v2-eyebrow" data-testid="eyebrow-pending-invites">
          <span className="v2-eyebrow__dot" aria-hidden />
          Pending invites
        </span>
        <span
          className="text-[10px] font-bold rounded-full px-2 py-0.5"
          style={{
            background: "var(--accent-neon-bg)",
            color: "var(--accent-neon-text)",
            border: "1px solid var(--accept-btn-ring)",
            boxShadow: "0 0 8px var(--accent-neon-bg)",
          }}
          data-testid="badge-pending-count"
        >
          {pending.length}
        </span>
      </div>
      <div className="space-y-2.5">
        {pending.map((p) => {
          const name = p.friend?.trainerName || p.friend?.displayName || p.friend?.username || "Unknown trainer";
          const handle = p.friend?.username ? `@${p.friend.username}` : null;
          const initial = firstInitial(name, "T");
          const busy = acceptMutation.isPending || declineMutation.isPending;
          return (
            <div
              key={p.id}
              data-testid={`pending-row-${p.id}`}
              className="v2-neon-card-soft p-3.5 flex items-center gap-3"
            >
              <Monogram letter={initial} hue={142} size={44} />
              <div className="min-w-0 flex-1">
                <p className="text-ink-strong text-sm font-bold truncate" data-testid={`pending-name-${p.id}`}>{name}</p>
                {handle && <p className="text-ink-faint text-[11px] truncate">{handle}</p>}
                <p className="text-ink-muted text-[10px] mt-0.5">wants to be your friend</p>
              </div>
              <button
                onClick={() => declineMutation.mutate(p.id)}
                disabled={busy}
                data-testid={`button-decline-${p.id}`}
                aria-label="Decline"
                className="surface-dark w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-50 transition"
              >
                <UserX className="text-ink-faint w-4 h-4" />
              </button>
              <button
                onClick={() => acceptMutation.mutate(p.id)}
                disabled={busy}
                data-testid={`button-accept-${p.id}`}
                aria-label="Accept"
                className="w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-50 transition"
                style={{
                  background: "var(--accent-fill)",
                  color: "var(--text-on-accent)",
                  boxShadow: "0 0 12px var(--accent-neon-bg)",
                  border: "1.5px solid var(--accept-btn-ring)",
                }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" strokeWidth={3} />}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   b14: counts-only dex summary chip on a live friend row. The only dex data
   that crosses friend-to-friend (privacy rule). Token-driven so it tracks
   light/dark. Silent while loading and on private/empty so the row stays calm.
   ════════════════════════════════════════════════════════════════════ */
type DexSummary = {
  caughtCount: number;
  shinyCount: number;
  luckyCount: number;
  perfectCount: number;
  topRegion: string | null;
};

// Reactive read of MY OWN dex counts from the device-local store, using the
// same deriveDexCounts source of truth as the Dex menu + Profile. Re-reads on
// every dex-state / dex-totals event so the You-vs-Them head-to-head never goes
// stale after a scan.
function useMyDexCounts() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setTick((n) => n + 1);
    window.addEventListener(DEX_TOTALS_EVENT, handler);
    window.addEventListener(DEX_STATE_EVENT, handler);
    return () => {
      window.removeEventListener(DEX_TOTALS_EVENT, handler);
      window.removeEventListener(DEX_STATE_EVENT, handler);
    };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => deriveDexCounts(), [tick]);
}

// Dex-type quick pills on a friend row. Each pill deep-links straight into the
// already-working Compare page with ?category= pre-applied, so we piggy-back on
// its live set-math (YOU HAVE / THEY HAVE) instead of duplicating logic here.
// Colours + labels mirror Compare's CATS so the two surfaces read as one.
const FRIEND_DEX_TYPES: { key: DexCategory; label: string; color: string }[] = [
  { key: "main",    label: "Main",  color: "hsl(var(--primary))" },
  { key: "shiny",   label: "Shiny", color: "hsl(var(--shiny))" },
  { key: "lucky",   label: "Lucky", color: "hsl(var(--lucky))" },
  { key: "perfect", label: "100%",  color: "hsl(var(--perfect))" },
  { key: "xxl",     label: "XXL",   color: "hsl(var(--xxl))" },
  { key: "xxs",     label: "XXS",   color: "hsl(var(--xxs))" },
];

function DexTypePills({ friendId }: { friendId: number }) {
  const [, setLocation] = useLocation();
  return (
    <div
      className="mt-2 flex items-center gap-1.5 overflow-x-auto"
      style={{ scrollbarWidth: "none" }}
      data-testid={`friend-dextype-pills-${friendId}`}
    >
      {FRIEND_DEX_TYPES.map((t) => (
        <button
          key={t.key}
          type="button"
          data-testid={`friend-dextype-${t.key}-${friendId}`}
          onClick={(e) => {
            // The whole row opens Main compare; stop the pill tap from also
            // firing that so category selection stays precise.
            e.stopPropagation();
            setLocation(`/friends/${friendId}/compare?category=${t.key}`);
          }}
          className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors"
          style={{
            background: "var(--surface-dark-7)",
            border: `1px solid ${t.color}`,
            color: t.color,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// You-vs-Them head-to-head bar now lives in a shared component so the Friends
// list and the Compare page header render it identically (see
// components/HeadToHeadBar).

function FriendDexSummaryChip({ friendId }: { friendId: number }) {
  const summaryQ = useQuery<{ private: boolean; summary: DexSummary | null }>({
    queryKey: [`/api/friends/${friendId}/dex-summary`],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const s = summaryQ.data?.summary;
  if (summaryQ.data?.private || !s || s.caughtCount === 0) return null;

  const stats: { label: string; value: number; color: string }[] = [
    { label: "caught", value: s.caughtCount, color: "var(--accent-fill)" },
    { label: "shiny", value: s.shinyCount, color: "hsl(var(--shiny))" },
    { label: "lucky", value: s.luckyCount, color: "hsl(var(--lucky))" },
    { label: "100%", value: s.perfectCount, color: "hsl(var(--perfect))" },
  ].filter((x) => x.value > 0);

  return (
    <div
      className="mt-1 flex items-center gap-2 flex-wrap"
      data-testid={`friend-dex-summary-${friendId}`}
    >
      {stats.map((x) => (
        <span key={x.label} className="inline-flex items-center gap-1 text-[10px] tabular-nums">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: x.color }} aria-hidden />
          <span className="font-semibold" style={{ color: x.color }}>{x.value}</span>
          <span className="text-muted-foreground">{x.label}</span>
        </span>
      ))}
      {s.topRegion && (
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {s.topRegion}
        </span>
      )}
    </div>
  );
}

/**
 * One round action on a friend row.
 *
 * A friend row leads to exactly two places — the head-to-head comparison and
 * the Trade Hub — and they now read as a matched pair rather than a word and a
 * chevron for one of them. Two tones so they are told apart at a glance without
 * reading: teal for comparing (the colour Compare has always worn here), Nexus
 * green for trading.
 *
 * Each stops propagation because these sit INSIDE the row's own click target;
 * without it a tap would fire the button and then the row behind it.
 */
function FriendRowAction({
  label,
  testId,
  tone,
  onClick,
  children,
}: {
  label: string;
  testId: string;
  tone: "teal" | "accent";
  onClick: () => void;
  children: React.ReactNode;
}) {
  const style =
    tone === "teal"
      ? {
          background: "var(--teal-fill-subtle)",
          border: "1px solid hsl(var(--teal) / 0.45)",
          color: "hsl(var(--teal))",
        }
      : {
          background: "var(--accent-subtle)",
          border: "1px solid var(--accent-fill-glow)",
          color: "var(--accent-fill)",
        };
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label}
      data-testid={testId}
      className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center hover-elevate active-elevate-2"
      style={style}
    >
      {children}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Live friends section, sourced from /api/friends. Empty-state first.
   ════════════════════════════════════════════════════════════════════ */
function LiveFriendsSection({ onOpenAdd }: { onOpenAdd: () => void }) {
  type LiveFriendRow = {
    id: number;
    friend: { id: number; username: string | null; displayName: string | null; trainerName: string | null } | null;
  };
  const [, setLocation] = useLocation();
  const { userPriorities } = useSession();
  const friendsQuery = useQuery<{ friends: LiveFriendRow[] }>({
    queryKey: ["/api/friends"],
  });
  const isLoading = friendsQuery.isLoading;

  // Favourited (priority) friends float to the TOP of the list, in pin order,
  // with everyone else following in the server's order — the same ordering the
  // Manage screen uses, so the list a trainer curated there is the list they see
  // here. Pinning is what makes a long friends list navigable, so it has to
  // apply on the surface people actually browse, not only in Manage.
  const pinnedIds = useMemo(() => priorityFriendIds(userPriorities), [userPriorities]);
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const friends = useMemo(
    () =>
      orderByPriority(friendsQuery.data?.friends ?? [], (f) => f.friend?.id ?? -1, pinnedIds),
    [friendsQuery.data, pinnedIds],
  );

  return (
    <section className="mt-7" data-testid="section-live-friends">
      <div className="flex items-center justify-between mb-3">
        <span className="v2-eyebrow" data-testid="eyebrow-your-friends">
          <span className="v2-eyebrow__dot" aria-hidden />
          Your friends
          {!isLoading && (
            <span
              className="text-ink-faint ml-1 font-mono text-[10px] tabular-nums"
            >
              {friends.length}
            </span>
          )}
        </span>
        <div className="flex items-center gap-3">
          {/* Manage is the way into the full list: pinned friends first, sorting,
              and removal. Kept beside "Add a friend" so the two list-level
              actions sit together rather than one hiding in Settings. */}
          <button
            onClick={() => setLocation("/friends/manage")}
            data-testid="button-manage-friends"
            className="text-ink-accent text-xs hover:underline inline-flex items-center gap-1"
          >
            <Settings2 className="w-3.5 h-3.5" /> Manage
          </button>
          <button
            onClick={onOpenAdd}
            data-testid="button-add-friend"
            className="text-ink-accent text-xs hover:underline inline-flex items-center gap-1"
          >
            <UserPlus className="w-3.5 h-3.5" /> Add a friend
          </button>
        </div>
      </div>

      {isLoading ? (
        <div
          className="text-ink-faint v2-neon-card-soft p-6 flex items-center justify-center"
        >
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          <span className="text-xs">Loading your friends...</span>
        </div>
      ) : friends.length === 0 ? (
        <div
          className="v2-neon-card p-6 text-center"
          data-testid="empty-live-friends"
        >
          <div
            className="w-14 h-14 mx-auto mb-3 relative"
            aria-hidden
          >
            <span
              className="absolute inset-0 v2-hex-frame"
              style={{ background: "var(--neon-hex-gradient)" }}
            />
            <span
              className="absolute v2-hex-frame"
              style={{ inset: 2, background: "var(--surface-dark-7)" }}
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <UserPlus className="text-ink-accent w-6 h-6" />
            </span>
          </div>
          <p className="text-ink-strong v2-display text-base">
            Add your trade circle
          </p>
          <p className="text-ink-faint text-[11px] mt-1.5 leading-relaxed">
            Send a request by trainer name, scan a QR, or paste a friend's invite code.
          </p>
          <button
            onClick={onOpenAdd}
            data-testid="button-empty-add-friend"
            className="v2-neon-cta mt-4 w-full py-3 text-sm inline-flex items-center justify-center gap-1.5"
          >
            <AtSign className="w-3.5 h-3.5" /> Add a friend
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {friends.map((f) => {
            const name = f.friend?.trainerName || f.friend?.displayName || f.friend?.username || "Trainer";
            const handle = f.friend?.username ? `@${f.friend.username}` : null;
            const initial = firstInitial(name, "T");
            // Compare keys off the friend's account id (the numeric id the
            // /api/friends/:id/compare endpoint expects), not the friendship
            // row id. Tapping a linked trainer opens the Dex comparison.
            const accountId = f.friend?.id ?? null;
            // Stable per-account hue so each avatar reads as a distinct trainer,
            // matching Compare.tsx's identity derivation (id * 47 % 360).
            const hue = accountId != null ? (accountId * 47) % 360 : 174;
            const openCompare = () => accountId != null && setLocation(`/friends/${accountId}/compare`);
            // A favourited friend wears the same accent frame + filled star the
            // Manage screen gives them, so the reason they're at the top of the
            // list is visible rather than mysterious.
            const isPriority = accountId != null && pinnedSet.has(accountId);
            // NOTE: this row is NOT a <button> because it now hosts the
            // dex-type pill buttons (HeadToHeadBar link + DexTypePills). Nested
            // <button> elements are invalid HTML, so the tappable region is a
            // role="button" div with an explicit inner header hit-area.
            return (
              <div
                key={f.id}
                data-testid={`live-friend-${f.id}`}
                data-priority={isPriority || undefined}
                className="rounded-2xl p-3 hover-elevate active-elevate-2"
                style={{
                  background: isPriority ? "var(--accent-fill-glow-faint)" : "var(--surface-raised)",
                  border: `1px solid ${isPriority ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
                }}
              >
                <div
                  role="button"
                  tabIndex={accountId == null ? -1 : 0}
                  aria-disabled={accountId == null}
                  aria-label={`Compare Dex with ${name}`}
                  onClick={openCompare}
                  onKeyDown={(e) => {
                    if (accountId == null) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openCompare();
                    }
                  }}
                  className="w-full text-left flex items-center gap-3 min-h-[44px] cursor-pointer aria-disabled:opacity-60 aria-disabled:cursor-not-allowed"
                >
                  <Monogram letter={initial} hue={hue} size={44} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate inline-flex items-center gap-1">
                      {isPriority && (
                        <Star
                          className="w-3 h-3 shrink-0"
                          style={{ color: "var(--accent-fill)", fill: "var(--accent-fill)" }}
                          aria-label="Favourite friend"
                        />
                      )}
                      <span className="truncate">{name}</span>
                    </p>
                    {handle && <p className="text-[11px] text-muted-foreground truncate">{handle}</p>}
                  </div>
                  {/* The row's two destinations, as a pair of round buttons.
                      They replaced a "Compare" word + chevron, which named only
                      one of the two things a friend row leads to and left the
                      other (trading) to a differently-shaped button beside it.
                      Real <button>s are valid here: the row is a role="button"
                      DIV precisely so it can host controls like these, and
                      stopPropagation keeps a tap from ALSO firing the row. */}
                  {accountId != null && (
                    <div className="shrink-0 flex items-center gap-1.5">
                      <FriendRowAction
                        label={`Compare Dex with ${name}`}
                        testId={`button-compare-${accountId}`}
                        tone="teal"
                        onClick={() => setLocation(`/friends/${accountId}/compare`)}
                      >
                        <span className="text-[10px] font-extrabold tracking-tight leading-none">VS</span>
                      </FriendRowAction>
                      <FriendRowAction
                        label={`Open trades with ${name}`}
                        testId={`button-trades-${accountId}`}
                        tone="accent"
                        onClick={() => setLocation(`/friends/${accountId}/trades`)}
                      >
                        <TradeSwapIcon className="w-4 h-4" strokeWidth={2} />
                      </FriendRowAction>
                    </div>
                  )}
                </div>
                {accountId != null && (
                  <>
                    <HeadToHeadBar friendId={accountId} />
                    <DexTypePills friendId={accountId} />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   v1.1.2-b5 SURFACE 9: full friends matrix Pro section.
   Basic shows just the name list above; Pro unlocks a side-by-side matrix
   that compares every friend's main/shiny/lucky/perfect totals at once.
   ════════════════════════════════════════════════════════════════════ */
function FriendsMatrixProSection() {
  const { tier } = useSession();
  const isPro = hasProAccess(tier);
  // Pro unlocks the real cross-trainer matrix. Basic keeps the ProLock upsell.
  // (Previously Pro rendered nothing — the matrix was never built — so upgrading
  // left this section blank.)
  if (isPro) return <FullFriendsMatrix />;
  return (
    <section className="mt-7" data-testid="section-friends-matrix">
      <span className="v2-eyebrow mb-3 inline-flex">
        <span className="v2-eyebrow__dot" aria-hidden />
        Full friends matrix
      </span>
      <div className="mt-2">
        <ProLock
          feature="friends-matrix"
          source="friends-matrix"
          blurb="See how every friend's dex stacks up against yours, side by side."
        />
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Full friends matrix (Pro). A side-by-side grid: your dex totals pinned at
   the top, then one row per friend with their Main / Shiny / Lucky / 100%
   counts and a ▲/▼ marker showing who's ahead. Each friend's counts come from
   the same counts-only /dex-summary endpoint the friend rows use (privacy-safe:
   nothing but totals ever crosses). Tapping a row opens the full Compare.
   ════════════════════════════════════════════════════════════════════ */
const MATRIX_COLS: {
  key: keyof Pick<DexSummary, "caughtCount" | "shinyCount" | "luckyCount" | "perfectCount">;
  mineKey: "main" | "shiny" | "lucky" | "perfect";
  label: string;
  color: string;
}[] = [
  { key: "caughtCount",  mineKey: "main",    label: "Main",  color: "hsl(var(--primary))" },
  { key: "shinyCount",   mineKey: "shiny",   label: "Shiny", color: "hsl(var(--shiny))" },
  { key: "luckyCount",   mineKey: "lucky",   label: "Lucky", color: "hsl(var(--lucky))" },
  { key: "perfectCount", mineKey: "perfect", label: "100%",  color: "hsl(var(--perfect))" },
];

const MATRIX_GRID = "grid grid-cols-[minmax(0,1.5fr)_repeat(4,minmax(2.2rem,1fr))] gap-1 items-center";

function FullFriendsMatrix() {
  type LiveFriendRow = {
    id: number;
    friend: { id: number; username: string | null; displayName: string | null; trainerName: string | null } | null;
  };
  const [, setLocation] = useLocation();
  const friendsQuery = useQuery<{ friends: LiveFriendRow[] }>({ queryKey: ["/api/friends"] });
  const mine = useMyDexCounts();
  const friends = (friendsQuery.data?.friends ?? []).filter((f) => f.friend?.id != null);

  return (
    <section className="mt-7" data-testid="section-friends-matrix">
      <span className="v2-eyebrow mb-3 inline-flex">
        <span className="v2-eyebrow__dot" aria-hidden />
        Full friends matrix
      </span>

      {friendsQuery.isLoading ? (
        <div className="text-ink-faint v2-neon-card-soft p-6 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          <span className="text-xs">Loading the matrix…</span>
        </div>
      ) : friends.length === 0 ? (
        <div className="v2-neon-card-soft p-6 text-center" data-testid="matrix-empty">
          <p className="text-ink-faint text-[11px] leading-relaxed">
            Add friends to see how every trainer's dex stacks up against yours here.
          </p>
        </div>
      ) : (
        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
          data-testid="friends-matrix-table"
        >
          {/* Column header */}
          <div className={`${MATRIX_GRID} px-3 py-2`} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <span className="text-[9px] uppercase tracking-wider font-bold text-ink-faint">Trainer</span>
            {MATRIX_COLS.map((c) => (
              <span
                key={c.key}
                className="text-[9px] uppercase tracking-wider font-bold text-center tabular-nums"
                style={{ color: c.color }}
              >
                {c.label}
              </span>
            ))}
          </div>

          {/* Your own totals, pinned as the baseline every row compares to. */}
          <div
            className={`${MATRIX_GRID} px-3 py-2.5`}
            style={{ background: "var(--accent-subtle)", borderBottom: "1px solid var(--border-subtle)" }}
            data-testid="matrix-row-you"
          >
            <span className="text-[12px] font-bold text-ink-accent truncate">You</span>
            {MATRIX_COLS.map((c) => (
              <span key={c.key} className="text-[13px] font-bold text-center tabular-nums text-ink-strong">
                {mine[c.mineKey]}
              </span>
            ))}
          </div>

          {friends.map((f, i) => (
            <MatrixFriendRow
              key={f.id}
              accountId={f.friend!.id}
              name={f.friend?.trainerName || f.friend?.displayName || f.friend?.username || "Trainer"}
              mine={mine}
              last={i === friends.length - 1}
              onOpen={() => setLocation(`/friends/${f.friend!.id}/compare`)}
            />
          ))}
        </div>
      )}

      <p className="text-[10px] text-ink-faint mt-2 leading-relaxed px-1">
        ▲ you're ahead · ▼ they're ahead. Tap a trainer to open the full compare.
      </p>
    </section>
  );
}

function MatrixFriendRow({
  accountId,
  name,
  mine,
  last,
  onOpen,
}: {
  accountId: number;
  name: string;
  mine: ReturnType<typeof useMyDexCounts>;
  last: boolean;
  onOpen: () => void;
}) {
  const summaryQ = useQuery<{ private: boolean; summary: DexSummary | null }>({
    queryKey: [`/api/friends/${accountId}/dex-summary`],
  });
  const s = summaryQ.data?.summary ?? null;
  const isPrivate = summaryQ.data?.private === true;
  const hue = (accountId * 47) % 360;
  const initial = firstInitial(name, "T");

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`matrix-row-${accountId}`}
      className={`${MATRIX_GRID} w-full text-left px-3 py-2.5 hover-elevate active-elevate-2`}
      style={last ? undefined : { borderBottom: "1px solid var(--border-subtle)" }}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Monogram letter={initial} hue={hue} size={26} />
        <MatrixTrainerName name={name} />
      </span>
      {MATRIX_COLS.map((c) => {
        if (summaryQ.isLoading) {
          return <span key={c.key} className="text-[12px] text-center text-ink-faint tabular-nums">·</span>;
        }
        if (isPrivate || !s) {
          return (
            <span key={c.key} className="text-[12px] text-center text-ink-faint tabular-nums" title={isPrivate ? "Private" : "No dex yet"}>
             
            </span>
          );
        }
        const theirs = s[c.key];
        // Arrow points from YOUR perspective and is theme-safe: ▲ green when
        // you're ahead, ▼ red when they are. Previously it read from the
        // friend's perspective (▲ = they're ahead) in the per-variant colour,
        // which flipped washed-out in light mode and confused the direction.
        const youAhead = theirs < mine[c.mineKey];
        const theyAhead = theirs > mine[c.mineKey];
        return (
          <span key={c.key} className="text-center tabular-nums inline-flex items-center justify-center gap-0.5">
            <span className="text-[13px] font-bold text-ink-strong">{theirs}</span>
            {youAhead && <span className="text-[9px]" style={{ color: "var(--accent-fill)" }}>▲</span>}
            {theyAhead && <span className="text-[9px]" style={{ color: "var(--danger-fill)" }}>▼</span>}
          </span>
        );
      })}
    </button>
  );
}

/**
 * A trainer's name in the matrix — truncated to fit, but scrollable so the rest
 * can be READ.
 *
 * The name column is the narrow one (the four count columns have to stay
 * legible), so on a small screen most names ellipsised at five or six
 * characters. That is fine until two trainers share a prefix — a friends list
 * with "Nexu…" twice tells you nothing about which row is which, and the row's
 * only other identifier is a monogram of the same first letter. Reported on
 * device 2026-08-12.
 *
 * Nudging the name sideways reveals it in place, which beats every alternative
 * that was considered: widening the column steals space from the numbers the
 * matrix exists for, and a tap-to-reveal collides with the tap that opens
 * Compare.
 *
 * TWO DETAILS MAKE IT WORK. A fade on the trailing edge, applied ONLY when the
 * name really is cut off, is the affordance — an invisible scroller is one
 * nobody uses. And a drag inside it must not open Compare on release: the
 * gesture is usually swallowed by the scroll, but "usually" would mean being
 * navigated away from the row you are trying to read, so the click is dropped
 * explicitly when the pointer travelled.
 */
export function MatrixTrainerName({ name }: { name: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [clipped, setClipped] = useState(false);
  const dragged = useRef(false);
  const startX = useRef(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setClipped(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [name]);

  return (
    <span
      ref={ref}
      // The row is a button, so this is a scrollable child inside it. Hidden
      // scrollbar (every other horizontal strip in the app hides its own) and
      // no wrapping, because the row height is shared with three number
      // columns.
      className="min-w-0 flex-1 overflow-x-auto no-scrollbar whitespace-nowrap text-[12px] font-semibold"
      title={name}
      data-testid="matrix-trainer-name"
      data-clipped={clipped || undefined}
      style={
        clipped
          ? {
              maskImage: "linear-gradient(to right, black calc(100% - 14px), transparent)",
              WebkitMaskImage: "linear-gradient(to right, black calc(100% - 14px), transparent)",
            }
          : undefined
      }
      onPointerDown={(e) => {
        dragged.current = false;
        startX.current = e.clientX;
      }}
      onPointerMove={(e) => {
        if (Math.abs(e.clientX - startX.current) > 6) dragged.current = true;
      }}
      onClickCapture={(e) => {
        if (!dragged.current) return;
        // Reading a name is not choosing a trainer.
        e.preventDefault();
        e.stopPropagation();
        dragged.current = false;
      }}
    >
      {name}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Monogram avatar
   ════════════════════════════════════════════════════════════════════ */
export function Monogram({ letter, hue, size = 36 }: { letter: string; hue: number; size?: number }) {
  // Hex-clipped trainer avatar with a neon green outer ring + subtle hue tint.
  // Used for friend rows, trade match avatars, trainer card portrait.
  // Backstop: a blank/whitespace letter (or a lone surrogate that slipped
  // through) would render an empty hexagon, so fall back to a visible glyph.
  const shown = letter && letter.trim() ? letter : "?";
  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size, filter: "drop-shadow(0 0 6px var(--accent-neon-bg))" }}
    >
      <span
        aria-hidden
        className="absolute inset-0 v2-hex-frame"
        style={{ background: "var(--neon-hex-gradient)" }}
      />
      <span
        aria-hidden
        className="absolute v2-hex-frame"
        style={{
          inset: Math.max(1.5, size * 0.04),
          background: `linear-gradient(135deg, hsl(${hue} 55% 35%), hsl(${hue} 50% 18%))`,
        }}
      />
      <span
        className="absolute inset-0 flex items-center justify-center font-extrabold v2-display"
        style={{
          fontSize: size * 0.44,
          color: "var(--monogram-letter)",
          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
        }}
      >
        {shown}
      </span>
    </div>
  );
}
